import {
    useEffect,
    Fragment,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useCodeViewerCopyContext,
    CodeViewerCopyContext
} from "../../context";
import type {
    ICodeCopyActionProps,
    CodeCopyStatus
} from "../../type";
import {
    CircleAlert as CircleAlertIcon,
    Check as CheckIcon,
    Copy as CopyIcon
} from "lucide-react-native";
import {
    getCodeCopyButtonMeasures,
    getCodeCopyStatusDelay
} from "../../stylesheet";
import {
    copyTextToClipboard
} from "../../../../utils";
import noticeStylesheet, {
    getNoticeLiveRegionStyle
} from "../../../notice/stylesheet";
import AnimatedIcon from "../../../animatedIcon";
import Tooltip from "../../../tooltip";
import Button from "../../../button";
import Text from "../../../text";

const CodeCopyIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const {
        shownCopyStatus,
        isTransitioned,
        customTheme
    } = useCodeViewerCopyContext();

    const isFailed = shownCopyStatus === "failed";

    const statusColor = isFailed ? "danger" : "success";

    return <AnimatedIcon
        customColor={isTransitioned ? undefined : customColor}
        toIcon={isFailed ? CircleAlertIcon : CheckIcon}
        color={isTransitioned ? statusColor : color}
        isTransitioned={isTransitioned}
        customTheme={customTheme}
        iconTransition="morph"
        fromIcon={CopyIcon}
        size={size}
    />;
};

const CodeCopyAction: FC<ICodeCopyActionProps> = ({
    isDisabled = false,
    type = "neutral",
    customLocalize,
    borderRadius,
    customTheme,
    inset,
    code
}) => {
    const {
        controlHeights,
        touchTargets,
        durations,
        radiuses,
        borders
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        copyStatus,
        setCopyStatus
    ] = useState<CodeCopyStatus>("idle");

    const copyStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const shownCopyStatusRef = useRef<CodeCopyStatus>("copied");

    const isMountedRef = useRef(true);

    if(copyStatus !== "idle") {
        shownCopyStatusRef.current = copyStatus;
    }

    const shownCopyStatus = shownCopyStatusRef.current;

    const copyButtonMeasures = getCodeCopyButtonMeasures({
        controlHeights,
        touchTargets,
        borderRadius,
        pointerType,
        radiuses,
        inset
    });

    const copyContextValue = useMemo(() => ({
        isTransitioned: copyStatus !== "idle",
        shownCopyStatus,
        customTheme
    }), [
        shownCopyStatus,
        customTheme,
        copyStatus
    ]);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;

            if(copyStatusTimerRef.current) {
                clearTimeout(copyStatusTimerRef.current);
            }
        };
    }, []);

    const onCopyPress = async () => {
        const isCopied = await copyTextToClipboard(code);

        if(!isMountedRef.current) {
            return;
        }

        if(copyStatusTimerRef.current) {
            clearTimeout(copyStatusTimerRef.current);
        }

        setCopyStatus(isCopied ? "copied" : "failed");

        if(Platform.OS === "ios") {
            AccessibilityInfo.announceForAccessibility(localize(isCopied ? "copied" : "copy-failed"));
        }

        copyStatusTimerRef.current = setTimeout(() => {
            copyStatusTimerRef.current = null;

            setCopyStatus("idle");
        }, getCodeCopyStatusDelay({
            durations
        }));
    };

    const renderCopyStatus = () => {
        if(copyStatus === "idle") {
            return null;
        }

        return <Text
            customTheme={customTheme}
            variant="labelSmallSize"
        >
            {localize(copyStatus === "copied" ? "copied" : "copy-failed")}
        </Text>;
    };

    const renderCopyButton = () => {
        const copyButton = <CodeViewerCopyContext.Provider
            value={copyContextValue}
        >
            <Button
                type={type === "danger" ? "danger" : "neutral"}
                borderRadius={copyButtonMeasures.borderRadius}
                accessibilityLabel={localize("copy")}
                style={{
                    margin: copyButtonMeasures.margin
                }}
                customTheme={customTheme}
                isDisabled={isDisabled}
                spreadBehaviour="free"
                onPress={onCopyPress}
                icon={CodeCopyIcon}
                variant="ghost"
                size="small"
            />
        </CodeViewerCopyContext.Provider>;

        if(isDisabled) {
            return copyButton;
        }

        return <Tooltip
            title={localize(shownCopyStatus === "failed" ? "copy-failed" : "copied")}
            customLocalize={customLocalize}
            isOpen={copyStatus !== "idle"}
            customTheme={customTheme}
            side="top"
        >
            <View>
                {copyButton}
            </View>
        </Tooltip>;
    };

    return <Fragment>
        <View
            style={[
                noticeStylesheet.liveRegion,
                getNoticeLiveRegionStyle({
                    borders
                })
            ]}
            aria-live="polite"
            role="status"
        >
            {renderCopyStatus()}
        </View>
        {renderCopyButton()}
    </Fragment>;
};
export default CodeCopyAction;
